import React from 'react';
import { howItWorks } from '../mock';
import { FileText, CreditCard, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const iconComponents = {
  FileText,
  CreditCard,
  CheckCircle
};

const HowItWorksSection = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-white via-[#012169]/5 to-white relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#F97316]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#012169]/10 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="px-6 py-2 bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white text-sm font-bold uppercase tracking-wider rounded-full shadow-lg">
              How It Works
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-[#012169] mb-4">
            Your Trademark Journey in 3 Easy Steps
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            From consultation to USPTO registration—we handle everything while you focus on growing your business
          </p>
        </div>

        {/* Horizontal Timeline - Desktop */}
        <div className="hidden md:block">
          <div className="relative">
            {/* Connecting Line */}
            <div className="absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-[#012169] via-[#F97316] to-[#8B5CF6]"></div>
            
            <div className="grid grid-cols-3 gap-8">
              {howItWorks.map((step, index) => {
                const IconComponent = iconComponents[step.icon];
                return (
                  <div key={step.id} className="relative group">
                    {/* Circle Node */}
                    <div className="flex justify-center mb-6">
                      <div className="relative">
                        <div className="w-20 h-20 bg-gradient-to-br from-[#012169] to-[#8B5CF6] rounded-full flex items-center justify-center shadow-2xl group-hover:scale-125 transition-all duration-500 border-4 border-white">
                          <span className="text-3xl font-black text-white">{index + 1}</span>
                        </div>
                        {/* Pulse Animation */}
                        <div className="absolute inset-0 rounded-full bg-[#F97316] opacity-0 group-hover:opacity-30 group-hover:scale-150 transition-all duration-500"></div>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="bg-white rounded-2xl p-6 shadow-xl group-hover:shadow-2xl transition-all duration-500 border-2 border-transparent group-hover:border-[#F97316] hover-3d-flip">
                      {/* Icon */}
                      <div className="w-16 h-16 bg-gradient-to-br from-[#F97316] to-[#EA580C] rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg group-hover:rotate-12 transition-all duration-500">
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-xl font-bold text-[#012169] mb-3 text-center group-hover:text-[#F97316] transition-colors">
                        {step.title}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-sm text-gray-600 leading-relaxed text-center">
                        {step.description}
                      </p>

                      {/* Arrow for flow */}
                      {index < howItWorks.length - 1 && (
                        <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 hidden lg:block">
                          <svg className="w-8 h-8 text-[#F97316]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile Vertical Layout */}
        <div className="md:hidden space-y-6">
          {howItWorks.map((step, index) => {
            const IconComponent = iconComponents[step.icon];
            return (
              <div key={step.id} className="relative">
                <div className="flex gap-4">
                  {/* Number Badge */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#012169] to-[#8B5CF6] rounded-full flex items-center justify-center shadow-xl border-4 border-white">
                      <span className="text-2xl font-black text-white">{index + 1}</span>
                    </div>
                    {index < howItWorks.length - 1 && (
                      <div className="w-0.5 h-20 bg-gradient-to-b from-[#012169] to-[#F97316] mx-auto mt-2"></div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-white rounded-2xl p-6 shadow-xl border-2 border-transparent hover:border-[#F97316] transition-all">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#F97316] to-[#EA580C] rounded-lg flex items-center justify-center mb-3 shadow-lg">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-[#012169] mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">Ready to protect your brand?</p>
          <div className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white font-bold rounded-full shadow-2xl hover:scale-110 transition-all duration-300 cursor-pointer">
            <span>Start Your Trademark Registration</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;